import React, { useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'

import urlJoin from 'url-join'

import { t } from 'src/translations'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import useWindowMessage from 'shared/js/hooks/useWindowMessage'

import { useCart } from 'public/components/online_ordering/CartContext'
import {
  useCheckout,
  OrderError
} from 'public/components/online_ordering/CheckoutContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import { usePayment } from 'public/components/online_ordering/PaymentContext'

import { resources } from 'config'

import { AdyenMessage } from './types'
import useAdyenPayments from './useAdyenPayments'

const AdyenPaymentIFrame = () => {
  const [height, setHeight] = useState('100%')
  const { ooRestaurant } = useRestaurant()
  const { cartGuid, refetchCart } = useCart()
  const { orderTotal, setOrderError, setShowAdyenOverlay } = useCheckout()
  const { lastKnownValidDeliveryAddress: deliveryAddress } = useDelivery()
  const { tipAmount } = usePayment()

  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })
  const email = useWatch({ name: 'yourInfoEmail' })
  const phone = useWatch({ name: 'yourInfoPhone' })

  const { placeAdyenOrder } = useAdyenPayments()

  useWindowMessage({
    origin: resources.sitesHost,
    sourceId: 'ADYEN_PAYMENT',
    messageHandlers: {
      [AdyenMessage.HEIGHT]: (data: { height: string }) =>
        setHeight(data.height),
      [AdyenMessage.SHOW_OVERLAY]: () => setShowAdyenOverlay(true),
      [AdyenMessage.HIDE_OVERLAY]: () => setShowAdyenOverlay(false),
      [AdyenMessage.ERROR]: (data: { error: OrderError }) =>
        setOrderError(data.error),
      [AdyenMessage.REFETCH_CART]: () => refetchCart(),
      [AdyenMessage.PLACE_ORDER]: (data: { paymentIntentID: string }) =>
        placeAdyenOrder(data.paymentIntentID)
    }
  })

  // Create a key based on delivery address to reload the payment form when address changes
  // (address can affect delivery fees and total amount)
  const deliveryAddressKey = deliveryAddress
    ? `${deliveryAddress.address1}-${deliveryAddress.city}-${deliveryAddress.state}-${deliveryAddress.zipCode}`
    : ''

  const src = useMemo(() => {
    const params = new URLSearchParams({
      shortUrl: ooRestaurant?.shortUrl ?? '',
      cartGuid: cartGuid ?? '',
      checkAmount: (orderTotal - tipAmount).toString(),
      tipAmount: tipAmount.toString(),
      email,
      phone,
      firstName,
      lastName,
      origin: typeof window !== 'undefined' ? window.location.origin : ''
    })

    return urlJoin(
      resources.sitesHost,
      'sites-web/v1/adyen',
      '?' + params.toString()
    )
  }, [
    ooRestaurant,
    firstName,
    lastName,
    email,
    phone,
    cartGuid,
    orderTotal,
    tipAmount
  ])

  // allow list for GPay and ApplePay
  return (
    <iframe
      key={deliveryAddressKey}
      title={t('adyen-payment-iframe.adyen-payment')}
      allow='payment; camera; microphone'
      width='100%'
      height={height}
      style={{ border: 0 }}
      src={src}
    />
  )
}

export default AdyenPaymentIFrame
